import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme, styled } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { ConfirmDialog } from "../../../components/primitives/feedback/ConfirmDialog";
import { useConfirmHandler } from "../../../hooks/useConfirmHandler";
import { LoadingState } from "../../../components/primitives/feedback/LoadingState";
import { EmptyState } from "../../../components/primitives/feedback/EmptyState";
import { MarkdownWithHighlight } from "../../../components/primitives/data-display/MarkdownWithHighlight";
import {
  FieldReviewPanel,
  FieldReviewTabs,
} from "../../../components/domain/annotation/FieldReviewPanel";
import { MultiVoterFieldPanel } from "../../../components/domain/annotation/FieldReviewPanel/MultiVoterFieldPanel";
import { PrevalidatedFieldPanel } from "../../../components/domain/annotation/FieldReviewPanel/PrevalidatedFieldPanel";
import { DocumentMetadataBadges } from "../../../components/domain/annotation/DocumentMetadataBadges";
import { useAnnotationReview } from "./useAnnotationReview";
import { InternalPage } from "../../../components/primitives/layout/InternalPage";
import { BottomSheetHandle } from "../../../components/primitives/surfaces/BottomSheetHandle";
import { ROUTES } from "../../../route_paths";

const DRAWER_BLEEDING = 56;
const RIGHT_PANEL_WIDTH = 400;
const COLLAPSE_STRIP_WIDTH = 32;

const CollapseStrip = styled(Stack)(({ theme }) => ({
  width: COLLAPSE_STRIP_WIDTH,
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  borderLeft: `1px solid ${theme.palette.divider}`,
  cursor: "pointer",
  "&:hover": { backgroundColor: theme.palette.action.hover },
}));

interface AnnotationSplitPaneProps {
  left: React.ReactNode;
  right: React.ReactNode;
  rightCollapsed: boolean;
  onToggleRight: () => void;
}

function AnnotationSplitPane({
  left,
  right,
  rightCollapsed,
  onToggleRight,
}: AnnotationSplitPaneProps) {
  return (
    <Stack direction="row" sx={{ flex: 1, overflow: "hidden" }}>
      <Stack sx={{ flex: 1, minWidth: 0, height: "100%", overflow: "hidden" }}>{left}</Stack>
      {rightCollapsed ? (
        <CollapseStrip onClick={onToggleRight}>
          <IconButton size="small" aria-label="Expand review panel">
            <ChevronLeftIcon />
          </IconButton>
        </CollapseStrip>
      ) : (
        <Stack
          direction="row"
          sx={{
            flex: `0 0 ${RIGHT_PANEL_WIDTH}px`,
            minWidth: 0,
            overflow: "hidden",
            borderLeft: "1px solid",
            borderColor: "divider",
          }}
        >
          <CollapseStrip
            onClick={onToggleRight}
            sx={{ borderLeft: "none", borderRight: "1px solid", borderColor: "divider" }}
          >
            <IconButton size="small" aria-label="Collapse review panel">
              <ChevronRightIcon />
            </IconButton>
          </CollapseStrip>
          <Stack sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>{right}</Stack>
        </Stack>
      )}
    </Stack>
  );
}

/** Full-height scrollable document pane for the mobile layout — fills the viewport below the
 *  Kodakku nav strip (the shared top NavBar is hidden in this section). */
function MobileDocumentPane({ children }: { children: React.ReactNode }) {
  return <Stack sx={{ height: "calc(100dvh - 44px)", overflow: "hidden" }}>{children}</Stack>;
}

/**
 * The annotation backlog stream: the primary Kodakku surface. Serves one (document, field)
 * work item at a time — either scoped to a single field (`?field=`) or the system's free pick.
 * Acting on the field (approve / override / mark empty) saves immediately and auto-ingests the
 * document inline if that was its last blocking Tier-1 field, then advances to the next item.
 */
export default function AnnotationReview() {
  const {
    scopedField,
    field,
    document,
    loading,
    error,
    saving,
    done,
    outcome,
    scorecardKind,
    monoScorecard,
    override,
    openOverride,
    approve,
    acceptCandidate,
    prevalidatedField,
    acceptPrevalidated,
    markEmpty,
    rejectAndAdvance,
    helpOpen,
    onHelpToggle,
  } = useAnnotationReview();

  const { handle: handleReject, dialogProps: rejectDialogProps } = useConfirmHandler({
    title: "Reject this document?",
    message:
      "This will permanently remove the document from the corpus. It will not appear in the annotation queue again.",
    confirmLabel: "Reject",
    confirmColor: "error",
  });

  const onReject = useCallback(
    () => handleReject(rejectAndAdvance),
    [handleReject, rejectAndAdvance]
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [rightCollapsed, setRightCollapsed] = useState(false);

  const [alert, setAlert] = useState<{
    severity: "success" | "info" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!outcome) return;
    setAlert({
      severity: outcome.status === "ingested" ? "success" : "info",
      message: `${outcome.status === "ingested" ? "Document ingested. " : "Still needs review. "}${outcome.reason}`,
    });
  }, [outcome]);

  useEffect(() => {
    if (error) setAlert({ severity: "error", message: error });
  }, [error]);

  // Memoized so the reference is stable across the frequent re-renders (override/help keystrokes)
  // — MarkdownWithHighlight is memoized, and a fresh node here would defeat that.
  const metadataBadges = useMemo(
    () => (document?.metadata ? <DocumentMetadataBadges metadata={document.metadata} /> : null),
    [document?.metadata]
  );

  if (loading && !document) {
    return <LoadingState label="Loading the next item…" />;
  }

  if (done) {
    return (
      <EmptyState
        title="Backlog is clear"
        message={
          scopedField
            ? "No more documents are blocked on this field."
            : "No documents are currently blocked on a Tier-1 field."
        }
        action={
          <Button component={Link} to={ROUTES.internal.annotation.path()} variant="outlined">
            Back to Annotation
          </Button>
        }
      />
    );
  }

  if (error && !document) {
    return <EmptyState title="Something went wrong" message={error} />;
  }

  if (!document || !field) return null;

  const monoPreAnnotation = monoScorecard?.[field] ?? {
    labels: [],
    quote: null,
    confidence: null,
  };

  // Evidence quote for the markdown highlight — a pre-validated field uses the pipeline's evidence;
  // mono uses the AI suggestion quote; multi uses the first candidate's evidence for the field.
  const highlightQuote = prevalidatedField
    ? prevalidatedField.evidence
    : scorecardKind === "multi"
      ? (Object.values(
          document.ai_scorecard?.kind === "multi" ? document.ai_scorecard.candidates : {}
        )[0]?.[field]?.evidence ?? null)
      : monoPreAnnotation.quote;

  // A multi document's field is one of: pre-validated (pipeline settled, shown pre-approved),
  // multi-candidate (competing model drafts), or mono (legacy single-voter).
  const activeReview =
    scorecardKind === "multi" && document.ai_scorecard?.kind === "multi" ? (
      prevalidatedField ? (
        <PrevalidatedFieldPanel
          field={field}
          prevalidated={prevalidatedField}
          override={override}
          saving={saving}
          helpOpen={helpOpen}
          onApprove={acceptPrevalidated}
          onOverride={openOverride}
          onMarkEmpty={markEmpty}
          onReject={onReject}
          onHelpToggle={onHelpToggle}
        />
      ) : (
        <MultiVoterFieldPanel
          field={field}
          candidates={document.ai_scorecard.candidates}
          consensus={document.ai_scorecard.consensus}
          override={override}
          saving={saving}
          helpOpen={helpOpen}
          onAcceptCandidate={acceptCandidate}
          onOverride={openOverride}
          onMarkEmpty={markEmpty}
          onReject={onReject}
          onHelpToggle={onHelpToggle}
        />
      )
    ) : (
      <FieldReviewPanel
        field={field}
        preAnnotation={monoPreAnnotation}
        override={override}
        saving={saving}
        helpOpen={helpOpen}
        onApprove={approve}
        onOverride={openOverride}
        onMarkEmpty={markEmpty}
        onReject={onReject}
        onHelpToggle={onHelpToggle}
      />
    );

  const reviewPanel = (
    <FieldReviewTabs
      scorecard={scorecardKind === "multi" ? null : monoScorecard}
      annotation={document.annotation}
      activeField={field}
      review={activeReview}
      sourceId={document.id}
    />
  );

  // Document reading surface, shared by the desktop split pane and the mobile full-screen pane.
  // Doc-level metadata badges render inside the sheet header, right after the source URL.
  const documentPane = (
    <MarkdownWithHighlight
      markdownText={document.markdown_text ?? ""}
      quote={highlightQuote}
      title={document.title ?? "(no title)"}
      sourceUrl={document.url}
      headerExtra={metadataBadges}
    />
  );

  const snackbar = alert && (
    <Snackbar
      open
      autoHideDuration={6000}
      onClose={() => setAlert(null)}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ width: "100%" }}>
        {alert.message}
      </Alert>
    </Snackbar>
  );

  if (isMobile) {
    return (
      <InternalPage width="full" sx={{ p: 0, flexGrow: 1 }}>
        <MobileDocumentPane>{documentPane}</MobileDocumentPane>

        <SwipeableDrawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
          swipeAreaWidth={DRAWER_BLEEDING}
          disableSwipeToOpen={false}
          keepMounted
          hideBackdrop
          PaperProps={{
            sx: {
              height: `calc(65% - ${DRAWER_BLEEDING}px)`,
              overflow: "visible",
            },
          }}
        >
          <BottomSheetHandle bleeding={DRAWER_BLEEDING} />
          <Stack sx={{ height: "100%", overflowY: "auto" }}>{reviewPanel}</Stack>
        </SwipeableDrawer>

        <ConfirmDialog {...rejectDialogProps} />
        {snackbar}
      </InternalPage>
    );
  }

  return (
    <InternalPage width="full" sx={{ p: 0, flexGrow: 1 }}>
      <Stack sx={{ height: "100vh", overflow: "hidden" }}>
        <AnnotationSplitPane
          left={documentPane}
          right={reviewPanel}
          rightCollapsed={rightCollapsed}
          onToggleRight={() => setRightCollapsed((v) => !v)}
        />
      </Stack>

      <ConfirmDialog {...rejectDialogProps} />
      {snackbar}
    </InternalPage>
  );
}
